import type { Locale as DateFnsLocale } from 'date-fns'
import { enUS, kk as kkLocale, ru as ruLocale } from 'date-fns/locale'

// ── Master registry ─────────────────────────────────────────────────────────
// All language codes used anywhere in the app.
// kz = app UI locale (convention); kk = ISO 639-1 Kazakh (backend content code).
// Use LOCALES.xxx as key when accessing any mapper: OlympiadLang[LOCALES.kz]
export const LOCALES = {
  ru: 'ru', // Russian
  en: 'en', // English
  kz: 'kz', // Kazakh — app UI locale code
  kk: 'kk', // Kazakh — ISO 639-1 / backend content code
  es: 'es', // Spanish
  de: 'de', // German
  az: 'az', // Azerbaijani
  ka: 'ka', // Georgian
} as const
export type Locale = (typeof LOCALES)[keyof typeof LOCALES]

export const LANG_LABELS: Record<Locale, string> = {
  [LOCALES.ru]: 'Русский',
  [LOCALES.kk]: 'Қазақша',
  [LOCALES.kz]: 'Қазақша',
  [LOCALES.en]: 'English',
  [LOCALES.es]: 'Español',
  [LOCALES.de]: 'Deutsch',
  [LOCALES.az]: 'Azərbaycanca',
  [LOCALES.ka]: 'ქართული',
}

export const LOCALE_TO_FLAG_COUNTRY: Record<Locale, string> = {
  [LOCALES.ru]: 'ru',
  [LOCALES.kz]: 'kz',
  [LOCALES.kk]: 'kz', // Kazakh language → Kazakhstan flag
  [LOCALES.en]: 'us', // Решение владельца 2026-09-22 (Q5): флаг США во всём приложении
  [LOCALES.es]: 'es',
  [LOCALES.de]: 'de',
  [LOCALES.az]: 'az',
  [LOCALES.ka]: 'ge', // Georgian language → Georgia flag
}
// ── Domain types ─────────────────────────────────────────────────────────────

export const AppLocale = {
  [LOCALES.ru]: LOCALES.ru,
  [LOCALES.en]: LOCALES.en,
  [LOCALES.kz]: LOCALES.kz,
} as const
export type AppLocale = (typeof AppLocale)[keyof typeof AppLocale]

// Content / task / study language (7 languages, uses ISO kk for Kazakh)
export const ContentLang = {
  [LOCALES.ru]: LOCALES.ru,
  [LOCALES.kk]: LOCALES.kk,
  [LOCALES.en]: LOCALES.en,
  [LOCALES.es]: LOCALES.es,
  [LOCALES.de]: LOCALES.de,
  [LOCALES.az]: LOCALES.az,
  [LOCALES.ka]: LOCALES.ka,
} as const
export type ContentLang = (typeof ContentLang)[keyof typeof ContentLang]

const CONTENT_LANGS = new Set<string>(Object.values(ContentLang))

/**
 * Пришедшее снаружи значение — язык контента или нет. Незнакомый код от
 * бэкенда или из `sessionStorage` не должен доходить до подписей и флагов.
 */
export function isContentLang(value: unknown): value is ContentLang {
  return typeof value === 'string' && CONTENT_LANGS.has(value)
}

/**
 * Запасной язык контента: дефолт `?language=` в контракте задач, и тот, на
 * котором уходит запрос, пока выбор участника неизвестен.
 */
export const DEFAULT_CONTENT_LANG: ContentLang = ContentLang.ru

// Olympiad language (backend full-word identifiers, keyed by UI locale)
export const OlympiadLang = {
  [LOCALES.ru]: 'russian',
  [LOCALES.kz]: 'kazakh',
  [LOCALES.en]: 'english',
  [LOCALES.ka]: 'georgian',
  [LOCALES.de]: 'german',
  [LOCALES.es]: 'spanish',
} as const
export type OlympiadLang = (typeof OlympiadLang)[keyof typeof OlympiadLang]

export const OLYMPIAD_LANG_OPTIONS: { value: OlympiadLang; label: string }[] = [
  { value: OlympiadLang[LOCALES.ru], label: 'RU' },
  { value: OlympiadLang[LOCALES.kz], label: 'KZ' },
  { value: OlympiadLang[LOCALES.en], label: 'EN' },
  { value: OlympiadLang[LOCALES.ka], label: 'KA' },
  { value: OlympiadLang[LOCALES.de], label: 'DE' },
  { value: OlympiadLang[LOCALES.es], label: 'ES' },
]

// Endonym chip labels for the olympiad wizard (Figma: short codes on the
// «Язык олимпиады» chips, full names on the description language tabs).
// Endonyms are locale-invariant, so they live here rather than in i18n files —
// same precedent as LANG_LABELS above.
export const OLYMPIAD_LANG_SHORT_LABELS: Record<OlympiadLang, string> = {
  [OlympiadLang[LOCALES.kz]]: 'Қаз',
  [OlympiadLang[LOCALES.ru]]: 'Рус',
  [OlympiadLang[LOCALES.en]]: 'Eng',
  [OlympiadLang[LOCALES.ka]]: 'ქარ',
  [OlympiadLang[LOCALES.de]]: 'Deu',
  [OlympiadLang[LOCALES.es]]: 'Esp',
}

export const OLYMPIAD_LANG_FULL_LABELS: Record<OlympiadLang, string> = {
  [OlympiadLang[LOCALES.kz]]: LANG_LABELS[LOCALES.kz],
  [OlympiadLang[LOCALES.ru]]: LANG_LABELS[LOCALES.ru],
  [OlympiadLang[LOCALES.en]]: LANG_LABELS[LOCALES.en],
  [OlympiadLang[LOCALES.ka]]: LANG_LABELS[LOCALES.ka],
  [OlympiadLang[LOCALES.de]]: LANG_LABELS[LOCALES.de],
  [OlympiadLang[LOCALES.es]]: LANG_LABELS[LOCALES.es],
}

// Flag shown on olympiad-language chips (Figma uses the US flag for English).
/** Қаз → Рус → Eng → ქარ → Deu → Esp — порядок языковых чипов во всех макетах */
export const OLYMPIAD_LANG_ORDER: OlympiadLang[] = [
  OlympiadLang[LOCALES.kz],
  OlympiadLang[LOCALES.ru],
  OlympiadLang[LOCALES.en],
  OlympiadLang[LOCALES.ka],
  OlympiadLang[LOCALES.de],
  OlympiadLang[LOCALES.es],
]

/** Языки старого контракта бэка — казахский, русский, английский. Это же языки,
 *  на которых бэк хранит адрес площадки и которые считаются языками олимпиады,
 *  если `languages` не прислан. Явный набор, а не срез порядка чипов. */
export const OLYMPIAD_CONTRACT_LANGS: OlympiadLang[] = [
  OlympiadLang[LOCALES.kz],
  OlympiadLang[LOCALES.ru],
  OlympiadLang[LOCALES.en],
]

export const OLYMPIAD_LANG_TO_FLAG_COUNTRY: Record<OlympiadLang, string> = {
  [OlympiadLang[LOCALES.kz]]: 'kz',
  [OlympiadLang[LOCALES.ru]]: 'ru',
  [OlympiadLang[LOCALES.en]]: 'us',
  [OlympiadLang[LOCALES.ka]]: 'ge',
  [OlympiadLang[LOCALES.de]]: 'de',
  [OlympiadLang[LOCALES.es]]: 'es',
}

// ── App locale mappers ────────────────────────────────────────────────────────

// API Accept-Language / Content-Language header value for the current UI locale.
export const LOCALE_TO_BACKEND_HEADER: Record<AppLocale, string> = {
  [LOCALES.ru]: LOCALES.ru,
  [LOCALES.en]: LOCALES.en,
  [LOCALES.kz]: LOCALES.kk, // kz → ISO 639-1 kk
}

// date-fns Locale objects
export const LOCALE_TO_DATE_FNS: Record<AppLocale, DateFnsLocale> = {
  [LOCALES.ru]: ruLocale,
  [LOCALES.en]: enUS,
  [LOCALES.kz]: kkLocale,
}

// BCP-47 tags for Intl.DateTimeFormat / toLocaleString
export const LOCALE_TO_INTL: Record<AppLocale, string> = {
  [LOCALES.ru]: 'ru-RU',
  [LOCALES.en]: 'en-US',
  [LOCALES.kz]: 'kk-KZ', // fixes bug: 'kz' is invalid BCP-47
}

// AppLocale → ContentLang (bridges UI kz to ISO kk)
// Uses `satisfies` to keep the narrow literal types while checking the constraint
export const LOCALE_TO_CONTENT_LANG = {
  [LOCALES.ru]: LOCALES.ru,
  [LOCALES.en]: LOCALES.en,
  [LOCALES.kz]: LOCALES.kk,
} as const satisfies Record<AppLocale, ContentLang>

// Browser navigator code → AppLocale (browser reports 'kk', app uses 'kz')
export const BROWSER_LANG_TO_LOCALE: Partial<Record<string, AppLocale>> = {
  [LOCALES.kk]: LOCALES.kz,
  [LOCALES.ru]: LOCALES.ru,
  [LOCALES.en]: LOCALES.en,
}

// ── Country/flag mappers ──────────────────────────────────────────────────────

// AppLocale → ISO 3166-1 alpha-2 country code for flagcdn.com

// ── UI option arrays ──────────────────────────────────────────────────────────

export const APP_SUPPORTED_LOCALES: AppLocale[] = [LOCALES.ru, LOCALES.kz, LOCALES.en]

export const APP_LANG_OPTIONS: { value: AppLocale; label: string; flagCountry: string }[] = [
  { value: LOCALES.ru, label: 'RU', flagCountry: LOCALE_TO_FLAG_COUNTRY[LOCALES.ru] },
  { value: LOCALES.kz, label: 'KZ', flagCountry: LOCALE_TO_FLAG_COUNTRY[LOCALES.kz] },
  { value: LOCALES.en, label: 'EN', flagCountry: LOCALE_TO_FLAG_COUNTRY[LOCALES.en] },
]
// ── Helper ────────────────────────────────────────────────────────────────────

export function toAppLocale(lang: string): AppLocale {
  const base = lang.split('-')[0]
  if (base in AppLocale) return base as AppLocale
  return BROWSER_LANG_TO_LOCALE[base] ?? LOCALES.ru
}

/** Коллатор языка интерфейса — единая сортировка подписей в выборе страны и города. */
export function uiCollator(language: string): Intl.Collator {
  return new Intl.Collator(LOCALE_TO_INTL[toAppLocale(language)])
}

export const CONTENT_LANG_OPTIONS: { code: ContentLang; label: string }[] = [
  { code: LOCALES.ru, label: LANG_LABELS[LOCALES.ru] },
  { code: LOCALES.kk, label: LANG_LABELS[LOCALES.kk] },
  { code: LOCALES.en, label: LANG_LABELS[LOCALES.en] },
  { code: LOCALES.es, label: LANG_LABELS[LOCALES.es] },
  { code: LOCALES.de, label: LANG_LABELS[LOCALES.de] },
  { code: LOCALES.az, label: LANG_LABELS[LOCALES.az] },
  { code: LOCALES.ka, label: LANG_LABELS[LOCALES.ka] },
]
